import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from '@tanstack/react-router';

export function useTableSelection<T extends { id: string }>(items: T[] = []) {
  const [selectedMap, setSelectedMap] = useState<Record<string, boolean>>({});
  const location = useLocation();

  // Clear selection when filters (searchParams) change
  useEffect(() => {
    setSelectedMap({});
  }, [location.search]);

  // Handle Escape key to clear selection
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSelectedMap({});
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const selectedIds = useMemo(
    () => Object.keys(selectedMap).filter((id) => selectedMap[id]),
    [selectedMap]
  );

  const selectedItems = useMemo(
    () => items.filter((item) => selectedMap[item.id]),
    [items, selectedMap]
  );

  const isAllSelected = items.length > 0 && selectedIds.length === items.length;
  const isSomeSelected = selectedIds.length > 0 && selectedIds.length < items.length;

  const toggleSelection = useCallback((id: string) => {
    setSelectedMap((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  }, []);

  const toggleAll = useCallback(
    (value: boolean) => {
      if (value) {
        const newMap: Record<string, boolean> = {};
        items.forEach((item) => {
          newMap[item.id] = true;
        });
        setSelectedMap(newMap);
      } else {
        setSelectedMap({});
      }
    },
    [items]
  );

  const clearSelection = useCallback(() => {
    setSelectedMap({});
  }, []);

  return {
    selectedMap,
    selectedIds,
    selectedItems,
    isAllSelected,
    isSomeSelected,
    toggleSelection,
    toggleAll,
    clearSelection,
  };
}
